import { useState } from "react";

export interface Artifact {
  code: string;
  language: string;
  title: string;
}

interface Props {
  artifact: Artifact;
  onClose: () => void;
}

export function ArtifactsPanel({ artifact, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(artifact.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleDownload = () => {
    const ext = LANG_EXTENSIONS[artifact.language] ?? artifact.language ?? "txt";
    const blob = new Blob([artifact.code], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `artifact.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="flex flex-col h-full flex-shrink-0"
      style={{
        width: 440,
        borderLeft: "1px solid var(--border)",
        background: "var(--bg-secondary)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="flex-shrink-0" style={{ color: "var(--accent)" }}>
            <path d="M3 1h6l3 3v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
            <path d="M9 1v3h3" />
          </svg>
          <span className="text-xs font-medium truncate" style={{ color: "var(--text-primary)" }}>
            {artifact.title}
          </span>
          {artifact.language && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded font-mono flex-shrink-0"
              style={{ background: "var(--accent-muted)", color: "var(--accent)" }}
            >
              {artifact.language}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Copy */}
          <button
            onClick={handleCopy}
            title="Copy"
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors"
            style={{ color: copied ? "#4ade80" : "var(--text-secondary)" }}
            onMouseEnter={(e) => { if (!copied) (e.currentTarget as HTMLElement).style.background = "var(--bg-tertiary)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
          >
            {copied ? (
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="2,7 5,10 11,3" /></svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="4" y="4" width="8" height="8" rx="1" />
                <path d="M9 4V2a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2" />
              </svg>
            )}
          </button>

          {/* Download */}
          <button
            onClick={handleDownload}
            title="Download"
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors"
            style={{ color: "var(--text-secondary)" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--bg-tertiary)"; (e.currentTarget as HTMLElement).style.color = "var(--text-primary)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)"; }}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M6.5 1v8M3.5 6l3 3 3-3" />
              <path d="M1 10v1a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-1" />
            </svg>
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            title="Close"
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors"
            style={{ color: "var(--text-secondary)" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--bg-tertiary)"; (e.currentTarget as HTMLElement).style.color = "var(--text-primary)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)"; }}
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.8">
              <line x1="1" y1="1" x2="10" y2="10" /><line x1="10" y1="1" x2="1" y2="10" />
            </svg>
          </button>
        </div>
      </div>

      {/* Code content */}
      <div className="flex-1 overflow-auto p-4">
        <pre
          className="text-xs font-mono leading-relaxed whitespace-pre overflow-x-auto"
          style={{ color: "var(--text-primary)" }}
        >
          {artifact.code}
        </pre>
      </div>
    </div>
  );
}

const LANG_EXTENSIONS: Record<string, string> = {
  typescript: "ts", javascript: "js", tsx: "tsx", jsx: "jsx",
  python: "py", rust: "rs", go: "go", java: "java",
  css: "css", html: "html", json: "json", yaml: "yml",
  bash: "sh", shell: "sh", sql: "sql", markdown: "md",
};
